import { FC } from 'react';
import { Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import {
  InputCheck,
  InputSelect,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import { Cut } from '../../types';

interface FreeSelectionFormValues {
  required?: boolean;
  minSelections?: number;
}

interface FreeSelectionQuestionFormProps {
  cuts?: Cut[];
  /** The capability set ID used to group uploaded images in S3. */
  capabilityId: string;
}

/**
 * Form for creating/editing Free Selection questions
 * Used with useModal pattern
 * @param props - Component props
 * @param props.cuts - Available cuts from capabilities
 * @param props.capabilityId - The capability set ID for S3 image grouping
 * @returns Free selection question form component
 */
export const FreeSelectionQuestionForm: FC<FreeSelectionQuestionFormProps> = ({
  cuts = [],
  capabilityId,
}) => {
  const { values } = useFormikContext<FreeSelectionFormValues>();
  const cutOptions = cuts.map((cut) => ({
    value: cut.id,
    label: cut.title,
  }));

  return (
    <Stack spacing={3}>
      <InputText label="Question Title" nameOverride="title" required />
      <InputTextArea
        label="Description"
        nameOverride="description"
        rows={2}
        hint="Optional description to help customers understand their options"
      />

      <InputCheck label="Required" nameOverride="required" />

      {values.required && (
        <Stack direction="row" spacing={2}>
          <InputText
            label="Minimum Selections"
            nameOverride="minSelections"
            type="number"
            hint="Minimum number of cuts the customer must select"
          />

          <InputText
            label="Maximum Selections"
            nameOverride="maxSelections"
            type="number"
            hint="Leave empty for unlimited"
          />
        </Stack>
      )}
    </Stack>
  );
};
